import {
  Fragment,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type Ref,
} from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Counter } from '@ui/counter';
import { KbdShortcut } from '@/components/ui/kbd';
import { ScrollableTabs } from '@/components/ui/scrollable-tabs';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { ChevronDownIcon, CloseIcon, FilterIcon, SubMenuArrowIcon } from '@components/icons';
import { cn } from '@/lib/utils';

import { useTranslation } from '@hooks/useTranslation';

import type { FilterOption } from '@components/DataTable';

export type {FilterOption};

interface FilterSelection {
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}

export interface FilterOptionGroup extends FilterSelection {
  key: string;
  label: string;
}

interface FilterCategoryBase {
  key: string;
  label: string;
}

export interface FlatFilterCategory extends FilterCategoryBase, FilterSelection {
  /** Adds a search field and a select-all footer to this category's panel. */
  searchable?: boolean;
}

export interface GroupedFilterCategory extends FilterCategoryBase {
  groups: FilterOptionGroup[];
}

export interface FilterOptionSection {
  key: string;
  label: string;
  options: FilterOption[];
}

/** A searchable panel with a tab and a list heading per section. */
export interface SectionedFilterCategory extends FilterCategoryBase, Omit<FilterSelection, 'options'> {
  sections: FilterOptionSection[];
}

export type FilterCategory = FlatFilterCategory | GroupedFilterCategory | SectionedFilterCategory;

type CategoryGroup = Omit<FilterOptionGroup, 'label'> & { label?: string };

// A flat category is one group without a header, so every reader of a
// category's selection walks the same shape.
export function categoryGroups(category: FilterCategory): CategoryGroup[] {
  if ('groups' in category) return category.groups;
  const {key, selected, onChange} = category;
  return [{key, options: categorySections(category).flatMap((section) => section.options), selected, onChange}];
}

type OptionSection = Omit<FilterOptionSection, 'label'> & { label?: string };

// A flat searchable category is one section without a heading, so the
// searchable panel renders both shapes the same way.
function categorySections(category: FlatFilterCategory | SectionedFilterCategory): OptionSection[] {
  if ('sections' in category) return category.sections;
  return [{key: category.key, options: category.options}];
}

export interface FilterByDropdownProps {
  categories: FilterCategory[];
  className?: string;
}

export function buildFilterOptions(
  t: (key: string) => string,
  i18nKey: string,
  values: readonly string[],
): FilterOption[] {
  return values.map((value) => ({value, label: t(`${i18nKey}.${value}`)}));
}

const SEARCHABLE_MAX_HEIGHT = 843;
const SUBMENU_SIDE_OFFSET = 12;
const SUBMENU_COLLISION_PADDING = 12;
const SUBMENU_INSTANT = 'data-[state=open]:animate-none data-[state=closed]:animate-none';

function toggleValue(selected: string[], value: string): string[] {
  return selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
}

interface CategoryTriggerProps {
  label: string;
  ref?: Ref<HTMLDivElement>;
  onActivate?: () => void;
}

function CategoryTrigger({ label, ref, onActivate }: CategoryTriggerProps) {
  return (
    <DropdownMenuSubTrigger
      ref={ref}
      className="hover:bg-transparent"
      onPointerEnter={(event) => {
        event.currentTarget.focus({preventScroll: true});
        onActivate?.();
      }}
      hideChevron
    >
      <span className="flex-1 text-left">{label}</span>
      <SubMenuArrowIcon aria-hidden="true" className="shrink-0 text-off-black" />
    </DropdownMenuSubTrigger>
  );
}

// Opts out of Radix's pointer-driven menu behaviour, which it guards behind
// `event.defaultPrevented`: focusing an item on pointer move, refocusing the
// menu content on pointer leave, and arming the submenu grace area.
const preventRadixPointerDefault = (event: PointerEvent<HTMLElement>) => event.preventDefault();

interface FilterRowProps {
  label: string;
  isSelected: boolean;
  onToggle: () => void;
  keepFocusOnHover?: boolean;
}

function FilterRow({ label, isSelected, onToggle, keepFocusOnHover }: FilterRowProps) {
  return (
    <DropdownMenuItem
      className="w-full gap-4 text-left text-[15px] leading-normal"
      onPointerMove={keepFocusOnHover ? preventRadixPointerDefault : undefined}
      onPointerLeave={keepFocusOnHover ? preventRadixPointerDefault : undefined}
      onSelect={(event) => {
        event.preventDefault();
        onToggle();
      }}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={onToggle}
        onClick={(event) => event.stopPropagation()}
        animated={false}
      />
      <span
        title={label}
        className={cn('min-w-0 flex-1 truncate text-left transition-colors', isSelected ? 'text-off-black' : 'text-grey')}
      >
        {label}
      </span>
    </DropdownMenuItem>
  );
}

interface SelectAllFooterProps {
  hasSelection: boolean;
  onSelectAll: () => void;
  onClear: () => void;
}

function SelectAllFooter({ hasSelection, onSelectAll, onClear }: SelectAllFooterProps) {
  const {t} = useTranslation();
  const label = hasSelection ? t('toolbar.clear_all') : t('toolbar.select_all');

  return (
    <div className="shrink-0 border-t border-dinnerplate-white p-1.5">
      <DropdownMenuItem
        className="group w-full justify-between p-4 hover:bg-transparent focus:bg-transparent"
        onSelect={(event) => {
          event.preventDefault();
          if (hasSelection) onClear();
          else onSelectAll();
        }}
      >
        <KbdShortcut
          keys={['⌥', hasSelection ? 'C' : 'A']}
          className="[&_kbd]:h-8 [&_kbd]:w-8 [&_kbd]:min-w-8 [&_kbd]:rounded-xs [&_kbd]:text-xs"
        />
        <span className="relative text-[15px] font-semibold leading-normal text-off-black">
          {label}
          <span className="absolute -bottom-px left-0 h-px w-full origin-left scale-x-0 bg-off-black transition-transform duration-150 ease-out group-hover:scale-x-100" />
        </span>
      </DropdownMenuItem>
    </div>
  );
}

// Alt+A / Alt+C, keyed off `code` because Option+A emits "å" on macOS.
function selectAllShortcut(onSelectAll: () => void, onClear: () => void) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (!event.altKey) return;
    if (event.code === 'KeyA') onSelectAll();
    else if (event.code === 'KeyC') onClear();
  };
}

function CheckboxGroup({ group }: { group: CategoryGroup }) {
  const {label, options, selected, onChange} = group;
  const labelId = useId();

  return (
    <DropdownMenuGroup aria-labelledby={label ? labelId : undefined} className="flex flex-col gap-2">
      {label ? <DropdownMenuLabel id={labelId}>{label}</DropdownMenuLabel> : null}
      {options.map((option) => (
        <FilterRow
          key={option.value}
          label={option.label}
          isSelected={selected.includes(option.value)}
          onToggle={() => onChange(toggleValue(selected, option.value))}
        />
      ))}
    </DropdownMenuGroup>
  );
}

interface CategorySubmenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CheckboxCategory({ category, open, onOpenChange }: { category: FilterCategory } & CategorySubmenuProps) {
  return (
    <DropdownMenuSub open={open} onOpenChange={onOpenChange}>
      <CategoryTrigger label={category.label} onActivate={() => onOpenChange(true)} />
      <DropdownMenuSubContent
        sideOffset={SUBMENU_SIDE_OFFSET}
        collisionPadding={SUBMENU_COLLISION_PADDING}
        className={cn('flex min-w-[194px] flex-col gap-2 border-[0.5px] p-2', SUBMENU_INSTANT)}
      >
        {categoryGroups(category).map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator className="m-0" /> : null}
            <CheckboxGroup group={group} />
          </Fragment>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

function SearchableSection({ section, selected, onChange }: { section: OptionSection } & Omit<FilterSelection, 'options'>) {
  const labelId = useId();

  return (
    <DropdownMenuGroup aria-labelledby={section.label ? labelId : undefined}>
      {section.label ? (
        <DropdownMenuLabel id={labelId}>{section.label}</DropdownMenuLabel>
      ) : null}
      {section.options.map((option) => (
        <FilterRow
          key={option.value}
          label={option.label}
          isSelected={selected.includes(option.value)}
          onToggle={() => onChange(toggleValue(selected, option.value))}
          keepFocusOnHover
        />
      ))}
    </DropdownMenuGroup>
  );
}

function SearchableCategory({
  category,
  open,
  onOpenChange,
}: { category: FlatFilterCategory | SectionedFilterCategory } & CategorySubmenuProps) {
  const {label, selected, onChange} = category;
  const {t} = useTranslation();
  const allTab = t('toolbar.filter_all_tab');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(allTab);
  const [placement, setPlacement] = useState({
    alignOffset: 0,
    maxHeight: SEARCHABLE_MAX_HEIGHT,
  });
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const sections = categorySections(category);
  const query = search.trim().toLowerCase();
  const matches = sections
    .filter((section) => activeTab === allTab || section.label === activeTab)
    .map((section) => ({
      ...section,
      options: section.options.filter((option) => option.label.toLowerCase().includes(query)),
    }))
    .filter((section) => section.options.length > 0);

  // The footer acts on the options in view, so a selection on another tab or
  // outside the search survives it.
  const visible = matches.flatMap((section) => section.options.map((option) => option.value));
  const selectAll = () => onChange([...new Set([...selected, ...visible])]);
  const clear = () => onChange(selected.filter((value) => !visible.includes(value)));

  // Escape closes the menu; Option combos are the submenu's shortcuts. Every
  // other key belongs to the input alone.
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' || event.altKey) return;
    event.stopPropagation();
  };

  const handleSearchBlur = (event: FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget as HTMLElement | null;
    if (next?.closest('[data-radix-collection-item]')) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  };

  // Radix aligns a submenu's top edge with its trigger row, and shifts the
  // whole panel up if it would overflow the viewport. Shifting it up by the
  // row's offset lines the two panels' tops up; capping the height to what is
  // left below that top keeps Radix from shifting it away again.
  const place = () => {
    const trigger = triggerRef.current;
    const menu = trigger?.closest('[role="menu"]');
    if (!trigger || !menu) return;

    const menuTop = menu.getBoundingClientRect().top;
    setPlacement({
      alignOffset: menuTop - trigger.getBoundingClientRect().top,
      maxHeight: Math.min(
        SEARCHABLE_MAX_HEIGHT,
        window.innerHeight - menuTop - SUBMENU_COLLISION_PADDING,
      ),
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (next) place();
    onOpenChange(next);
  };

  return (
    <DropdownMenuSub open={open} onOpenChange={handleOpenChange}>
      <CategoryTrigger ref={triggerRef} onActivate={() => handleOpenChange(true)} label={label} />
      <DropdownMenuSubContent
        sideOffset={SUBMENU_SIDE_OFFSET}
        alignOffset={placement.alignOffset}
        collisionPadding={SUBMENU_COLLISION_PADDING}
        onMouseEnter={() => searchRef.current?.focus()}
        onKeyDown={selectAllShortcut(selectAll, clear)}
        style={{maxHeight: placement.maxHeight}}
        className={cn('flex w-[280px] flex-col overflow-hidden p-0', SUBMENU_INSTANT)}
      >
        <div className="relative shrink-0 overflow-hidden rounded-t-input border-b border-dinnerplate-white bg-off-white">
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            placeholder={t('toolbar.filter_search_placeholder')}
            aria-label={t('toolbar.filter_search_placeholder')}
            className={cn(
              'h-[53px] w-full px-6',
              'border-none bg-transparent',
              'font-inter text-sm leading-normal text-off-black placeholder:text-grey-light',
              'focus:outline-none focus:ring-0',
              'transition-colors',
            )}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-6 top-1/2 -translate-y-1/2 p-1 text-grey transition-colors hover:text-off-black"
              aria-label="Clear search"
            >
              <CloseIcon className="size-4" />
            </button>
          )}
        </div>
        {'sections' in category ? (
          <SearchableDropdown.Tabs>
            <ScrollableTabs
              tabs={[allTab, ...category.sections.map((section) => section.label)]}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </SearchableDropdown.Tabs>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
          {matches.length === 0 ? (
            <p className="px-6 py-3 text-center text-sm leading-normal text-grey">
              {t('toolbar.filter_no_results')}
            </p>
          ) : (
            matches.map((section) => (
              <SearchableSection key={section.key} section={section} selected={selected} onChange={onChange} />
            ))
          )}
        </div>
        <SelectAllFooter
          hasSelection={visible.some((value) => selected.includes(value))}
          onSelectAll={selectAll}
          onClear={clear}
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

export function FilterByDropdown({ categories, className }: FilterByDropdownProps) {
  const {t} = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const submenuProps = (key: string): CategorySubmenuProps => ({
    open: activeCategory === key,
    onOpenChange: (open) => setActiveCategory((current) => (open ? key : current === key ? null : current)),
  });

  const appliedCount = categories
    .flatMap(categoryGroups)
    .reduce((total, group) => total + group.selected.length, 0);

  return (
    <div className="relative inline-flex">
      <DropdownMenu onOpenChange={(open) => !open && setActiveCategory(null)}>
        <DropdownMenuTrigger
          className={cn(
            'group flex h-auto cursor-pointer select-none items-center justify-between gap-3 rounded-input',
            'border border-dinnerplate-white bg-off-white px-6 py-4',
            'font-inter text-sm font-semibold leading-[1.5] text-off-black',
            'focus:outline-none focus-visible:outline-none',
            className,
          )}
        >
          <span className="flex flex-1 items-center gap-1.5 text-left">
            <FilterIcon className="shrink-0" />
            {t('toolbar.filter_by')}
          </span>
          <ChevronDownIcon
            aria-hidden="true"
            className="shrink-0 text-off-black chevron-motion group-data-[state=open]:rotate-180"
          />
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          sideOffset={6}
          className="flex min-w-[204px] w-auto flex-col p-1.5"
        >
          {categories.map((category) =>
            'sections' in category || ('options' in category && category.searchable) ? (
              <SearchableCategory key={category.key} category={category} {...submenuProps(category.key)} />
            ) : (
              <CheckboxCategory key={category.key} category={category} {...submenuProps(category.key)} />
            ),
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {appliedCount > 0 && (
        <Counter
          value={appliedCount}
          className="absolute -top-1.5 -right-1.5"
          aria-label={t('toolbar.filters_applied', {count: appliedCount})}
        />
      )}
    </div>
  );
}
